import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  Button,
  Container,
  Grid,
  Header,
  Icon,
  Message,
  Segment,
  Table,
  Loader,
  Dimmer,
  Modal,
} from "semantic-ui-react";
import { BackOfficeRoutes } from "@routes/urls";
import { Link } from "react-router-dom";
import {
  removeDocumentFromSelection,
  clearDocumentSelection,
  updateDocumentInSelection,
} from "./state/actions";
import PrintLabelsUtil from "./hooks/UsePrintLabels";
import { DocumentLabelsSelectedCard } from "./DocumentLabelsSelectedCard";
import { documentApi } from "@api/documents";

class DocumentLabelsSelectedComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      showMissingCallNumberDialog: false,
      documentsWithPhysicalCopies: [],
      loadingPhysicalCopies: true,
      physicalCopiesError: null,
      selectedCopiesForPrinting: {},
      showCopySelectionMode: false,
    };
  }

  componentDidMount() {
    window.addEventListener("documentUpdated", this.handleDocumentUpdated);
    this.checkForRecentUpdates();
    this.fetchPhysicalCopiesData();
    this.loadCopySelectionState();
  }

  componentWillUnmount() {
    window.removeEventListener("documentUpdated", this.handleDocumentUpdated);
    this.saveCopySelectionState();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selectedDocuments !== this.props.selectedDocuments) {
      this.fetchPhysicalCopiesData();
    }
  }

  fetchPhysicalCopiesData = async () => {
    const { selectedDocuments } = this.props;

    if (selectedDocuments.length === 0) {
      this.setState({
        documentsWithPhysicalCopies: [],
        loadingPhysicalCopies: false,
      });
      return;
    }

    this.setState({ loadingPhysicalCopies: true, physicalCopiesError: null });

    try {
      const documentsWithDetails = await Promise.all(
        selectedDocuments.map(async (selectedDoc) => {
          try {
            const response = await documentApi.get(selectedDoc.pid);
            const copiesDetails = documentApi.getPhysicalCopiesDetails(
              response.data
            );
            return { ...selectedDoc, ...copiesDetails, fetchError: null };
          } catch {
            return {
              ...selectedDoc,
              fetchError: `Failed to load details for ${
                selectedDoc.title || selectedDoc.pid
              }`,
              totalPhysicalCopies: 0,
              physicalCopies: [],
            };
          }
        })
      );

      this.setState({
        documentsWithPhysicalCopies: documentsWithDetails,
        loadingPhysicalCopies: false,
      });
    } catch {
      this.setState({
        physicalCopiesError: "Failed to load physical copies data",
        loadingPhysicalCopies: false,
      });
    }
  };

  hasDocumentsWithMissingCallNumbers = (documents) => {
    return documents.some((doc) => {
      const callNumber = doc.call_number || doc.metadata?.call_number;
      return !callNumber || callNumber === "" || callNumber === "N/A";
    });
  };

  getMissingCallNumberCount = (documents) => {
    return documents.filter((doc) => {
      const callNumber = doc.call_number || doc.metadata?.call_number;
      return !callNumber || callNumber === "" || callNumber === "N/A";
    }).length;
  };

  handlePrintLabels = () => {
    if (!this.hasSelectedCopies()) {
      alert("Please select at least one copy to print labels for.");
      return;
    }

    const selectedCopiesData = this.prepareSelectedCopiesForPrinting();
    PrintLabelsUtil.printLabels(selectedCopiesData);
  };

  handleCancelPrint = () => {
    this.setState({ showMissingCallNumberDialog: false });
  };

  prepareSelectedCopiesForPrinting = () => {
    const { documentsWithPhysicalCopies, selectedCopiesForPrinting } =
      this.state;
    const selectedCopiesData = [];

    Object.entries(selectedCopiesForPrinting).forEach(
      ([docPid, copyIndices]) => {
        const document = documentsWithPhysicalCopies.find(
          (doc) => doc.pid === docPid
        );
        if (document?.physicalCopies) {
          copyIndices.forEach((copyIndex) => {
            if (document.physicalCopies[copyIndex]) {
              const callNumberPrefix =
                document.documentInfo?.callNumberPrefix ||
                document.call_number_prefix ||
                document.metadata?.call_number_prefix ||
                "";

              const baseCallNumber =
                document.documentInfo?.callNumber ||
                document.call_number ||
                document.metadata?.call_number ||
                "N/A";

              const copy = document.physicalCopies[copyIndex];
              const suffix = copy.call_number_suffix || copy.callNumberSuffix;

              let displayCallNumber = baseCallNumber;
              if (baseCallNumber !== "N/A") {
                // Add prefix if it exists
                if (callNumberPrefix) {
                  displayCallNumber = `${callNumberPrefix} ${baseCallNumber}`;
                }
                // Add suffix if it exists
                if (suffix) {
                  displayCallNumber = `${displayCallNumber} ${suffix}`;
                }
              }

              selectedCopiesData.push({
                ...document,
                copyNumber: copyIndex + 1,
                callNumberWithCopy: displayCallNumber,
                physicalCopy: copy,
                title: document.documentInfo?.title || document.title,
              });
            }
          });
        }
      }
    );

    return selectedCopiesData;
  };

  handleConfirmPrint = () => {
    this.setState({ showMissingCallNumberDialog: false });
    PrintLabelsUtil.printLabels(this.props.selectedDocuments);
  };

  handleCopySelection = (docPid, copyIndex, isSelected) => {
    this.setState((prevState) => {
      const newSelectedCopies = { ...prevState.selectedCopiesForPrinting };

      if (!newSelectedCopies[docPid]) {
        newSelectedCopies[docPid] = [];
      }

      if (isSelected) {
        if (!newSelectedCopies[docPid].includes(copyIndex)) {
          newSelectedCopies[docPid].push(copyIndex);
        }
      } else {
        newSelectedCopies[docPid] = newSelectedCopies[docPid].filter(
          (index) => index !== copyIndex
        );
        if (newSelectedCopies[docPid].length === 0) {
          delete newSelectedCopies[docPid];
        }
      }

      setTimeout(() => this.saveCopySelectionState(), 0);
      return { selectedCopiesForPrinting: newSelectedCopies };
    });
  };

  toggleCopySelectionMode = () => {
    this.setState((prevState) => {
      const newShowCopySelectionMode = !prevState.showCopySelectionMode;
      const newSelectedCopies = newShowCopySelectionMode
        ? prevState.selectedCopiesForPrinting
        : {};

      setTimeout(() => this.saveCopySelectionState(), 0);

      return {
        showCopySelectionMode: newShowCopySelectionMode,
        selectedCopiesForPrinting: newSelectedCopies,
      };
    });
  };

  selectAllCopiesForDocument = (docPid, totalCopies) => {
    this.setState((prevState) => {
      const newSelectedCopies = { ...prevState.selectedCopiesForPrinting };
      newSelectedCopies[docPid] = Array.from(
        { length: totalCopies },
        (_, i) => i
      );
      return { selectedCopiesForPrinting: newSelectedCopies };
    });
  };

  deselectAllCopiesForDocument = (docPid) => {
    this.setState((prevState) => {
      const newSelectedCopies = { ...prevState.selectedCopiesForPrinting };
      delete newSelectedCopies[docPid];
      return { selectedCopiesForPrinting: newSelectedCopies };
    });
  };

  getSelectedCopiesCount = () => {
    return Object.values(this.state.selectedCopiesForPrinting).reduce(
      (total, copies) => total + copies.length,
      0
    );
  };

  hasSelectedCopies = () => this.getSelectedCopiesCount() > 0;

  saveCopySelectionState = () => {
    const { selectedCopiesForPrinting, showCopySelectionMode } = this.state;
    if (
      showCopySelectionMode ||
      Object.keys(selectedCopiesForPrinting).length > 0
    ) {
      const selectionState = {
        selectedCopiesForPrinting,
        showCopySelectionMode,
        timestamp: Date.now(),
      };
      sessionStorage.setItem(
        "documentLabelCopySelection",
        JSON.stringify(selectionState)
      );
    }
  };

  loadCopySelectionState = () => {
    try {
      const savedState = sessionStorage.getItem("documentLabelCopySelection");
      if (savedState) {
        const { selectedCopiesForPrinting, showCopySelectionMode, timestamp } =
          JSON.parse(savedState);
        const timeDiff = Date.now() - timestamp;

        if (timeDiff < 600000) {
          this.setState({
            selectedCopiesForPrinting: selectedCopiesForPrinting || {},
            showCopySelectionMode: showCopySelectionMode || false,
          });
        } else {
          sessionStorage.removeItem("documentLabelCopySelection");
        }
      }
    } catch {
      sessionStorage.removeItem("documentLabelCopySelection");
    }
  };

  checkForRecentUpdates = () => {
    try {
      const lastUpdatedData = sessionStorage.getItem("lastUpdatedDocument");
      if (lastUpdatedData) {
        const { timestamp, document } = JSON.parse(lastUpdatedData);
        const timeDiff = Date.now() - timestamp;

        if (timeDiff < 30000) {
          const isSelected = this.props.selectedDocuments.some(
            (doc) => doc.pid === document.pid
          );
          if (isSelected) {
            this.props.updateDocumentInSelection(document);
            sessionStorage.removeItem("lastUpdatedDocument");
          }
        } else {
          sessionStorage.removeItem("lastUpdatedDocument");
        }
      }
    } catch {
      sessionStorage.removeItem("lastUpdatedDocument");
    }
  };

  handleDocumentUpdated = (event) => {
    const updatedDocument = event.detail;
    const isSelected = this.props.selectedDocuments.some(
      (doc) => doc.pid === updatedDocument.pid
    );

    if (isSelected) {
      this.props.updateDocumentInSelection(updatedDocument);
      this.fetchPhysicalCopiesData();
    }
  };

  renderPhysicalCopiesCell = (doc) => {
    const { showCopySelectionMode } = this.state;
    const callNumberPrefix =
      doc.documentInfo?.callNumberPrefix ||
      doc.call_number_prefix ||
      doc.metadata?.call_number_prefix ||
      "";
    const baseCallNumber =
      doc.documentInfo?.callNumber ||
      doc.call_number ||
      doc.metadata?.call_number ||
      "Call Number not specified";

    if (!doc.physicalCopies?.length) {
      return <div className="no-copies-message">No physical copies found</div>;
    }

    return (
      <div className={showCopySelectionMode ? "copy-selection-mode" : ""}>
        <div style={{ display: "flex", alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}>
            {doc.physicalCopies.map((copy, copyIndex) => {
              // Build call number with prefix and suffix if available
              let displayCallNumber = baseCallNumber;
              if (baseCallNumber !== "Call Number not specified") {
                // Add prefix if it exists
                if (callNumberPrefix) {
                  displayCallNumber = `${callNumberPrefix} ${baseCallNumber}`;
                }
                // Add suffix if it exists
                const suffix = copy.call_number_suffix || copy.callNumberSuffix;
                if (suffix) {
                  displayCallNumber = `${displayCallNumber} ${suffix}`;
                }
              }

              const isSelected =
                this.state.selectedCopiesForPrinting[doc.pid]?.includes(
                  copyIndex
                ) || false;

              return (
                <div key={copyIndex} className="copy-item">
                  {showCopySelectionMode && (
                    <div className="copy-checkbox">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) =>
                          this.handleCopySelection(
                            doc.pid,
                            copyIndex,
                            e.target.checked
                          )
                        }
                        style={{ transform: "scale(1.2)" }}
                      />
                    </div>
                  )}
                  <div className="copy-details">
                    <span className="copy-callnumber">{displayCallNumber}</span>
                    <br />
                    <small style={{ color: "#666" }}>
                      Status:{" "}
                      <span
                        className={
                          copy.circulationStatus === "CAN_CIRCULATE"
                            ? "status-available"
                            : "status-other"
                        }
                      >
                        {copy.circulationStatus}
                      </span>
                      {copy.shelf !== "Not specified" && (
                        <> | Shelf: {copy.shelf}</>
                      )}
                      {copy.medium !== "NOT_SPECIFIED" && (
                        <> | Medium: {copy.medium}</>
                      )}
                    </small>
                  </div>
                </div>
              );
            })}
          </div>
          {showCopySelectionMode && (
            <div
              className="copy-controls"
              style={{
                marginLeft: "12px",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
              }}
            >
              <Button
                size="mini"
                icon="check"
                onClick={() =>
                  this.selectAllCopiesForDocument(
                    doc.pid,
                    doc.physicalCopies.length
                  )
                }
                color="blue"
                title="Select All Copies"
                style={{ padding: "4px", minWidth: "24px", height: "24px" }}
              />
              <Button
                size="mini"
                icon="x"
                onClick={() => this.deselectAllCopiesForDocument(doc.pid)}
                title="Deselect All Copies"
                style={{ padding: "4px", minWidth: "24px", height: "24px" }}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  render() {
    const {
      selectedDocuments,
      removeDocumentFromSelection,
      clearDocumentSelection,
    } = this.props;
    const {
      documentsWithPhysicalCopies,
      loadingPhysicalCopies,
      physicalCopiesError,
      showCopySelectionMode,
    } = this.state;

    const tableStyles = `
      .document-labels-table { background: white; border: 2px solid #d4d4d5; border-radius: 6px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
      .document-labels-table .ui.table { border: none; border-radius: 0; margin: 0; }
      .document-labels-table .ui.table td, .document-labels-table .ui.table th { color: #333333 !important; border-right: 1px solid #d4d4d5 !important; border-bottom: 1px solid #d4d4d5 !important; padding: 14px 12px !important; vertical-align: middle !important; }
      .document-labels-table .ui.table td:last-child, .document-labels-table .ui.table th:last-child { border-right: none !important; }
      .document-labels-table .ui.table tbody tr:last-child td { border-bottom: none !important; }
      .document-labels-table .ui.table th { background-color: #f8f9fa !important; font-weight: 600 !important; text-align: center !important; border-top: none !important; }
      .document-labels-table .ui.table tbody tr { border-bottom: 2px solid #d4d4d5 !important; }
      .document-labels-table .ui.table tbody tr:last-child { border-bottom: none !important; }
      .document-labels-table .ui.table tbody tr:hover { background-color: #f8f9fa !important; }
      .document-labels-table .title-column { max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 500; }
      .document-labels-table .physical-copies-cell { padding: 12px !important; }
      .document-labels-table .copy-item { display: block; padding: 6px 0; border-bottom: 1px solid #f0f0f0; }
      .document-labels-table .copy-item:last-child { border-bottom: none; }
      .document-labels-table .copy-callnumber { color: #333333; font-weight: 600; }
      .document-labels-table .status-available { color: #2e7d32; background-color: #e6f4ea; padding: 3px 8px; border-radius: 4px; font-weight: 500; }
      .document-labels-table .status-other { color: #666; background-color: #f1f3f4; padding: 3px 8px; border-radius: 4px; font-weight: 500; }
      .document-labels-table .action-buttons { display: flex; gap: 6px; justify-content: center; align-items: center; }
      .document-labels-table .action-buttons .ui.button { min-width: 32px; height: 32px; padding: 8px !important; display: flex; align-items: center; justify-content: center; }
      .document-labels-table .action-buttons .ui.button i.icon { margin: 0 !important; }
      .document-labels-table .no-copies-message { color: #9f9ea9; font-style: italic; padding: 8px 0; }
      .copy-selection-mode .copy-item { display: flex; align-items: center; padding: 8px 0; border-bottom: 1px solid #f0f0f0; }
      .copy-selection-mode .copy-checkbox { margin-right: 12px; }
      .copy-selection-mode .copy-details { flex: 1; }
    `;

    return (
      <>
        <Header as="h2">Selected Documents for Label Generation</Header>
        <Container fluid>
          <Grid>
            <Grid.Row>
              <Grid.Column width={16}>
                <Button
                  as={Link}
                  to={BackOfficeRoutes.documentLabels}
                  icon
                  labelPosition="left"
                >
                  <Icon name="arrow left" />
                  Back to Search
                </Button>
                {selectedDocuments.length > 0 && (
                  <>
                    <Button
                      size="small"
                      color="red"
                      icon
                      labelPosition="left"
                      floated="right"
                      onClick={clearDocumentSelection}
                    >
                      <Icon name="trash" />
                      Clear All Documents
                    </Button>
                    <Button
                      size="small"
                      color={showCopySelectionMode ? "green" : "blue"}
                      icon
                      labelPosition="left"
                      floated="right"
                      onClick={this.toggleCopySelectionMode}
                      style={{ marginRight: "10px" }}
                    >
                      <Icon
                        name={showCopySelectionMode ? "list" : "check square"}
                      />
                      {showCopySelectionMode
                        ? "Show All Copies"
                        : "Select Specific Copies"}
                    </Button>
                  </>
                )}
              </Grid.Column>
            </Grid.Row>

            {selectedDocuments.length === 0 ? (
              <Grid.Row>
                <Grid.Column width={16}>
                  <Segment placeholder textAlign="center">
                    <Header icon>
                      <Icon name="tags" />
                      No documents selected for label generation
                    </Header>
                    <Button
                      as={Link}
                      to={BackOfficeRoutes.documentLabels}
                      primary
                    >
                      Select Documents
                    </Button>
                  </Segment>
                </Grid.Column>
              </Grid.Row>
            ) : (
              <>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Header as="h3">
                      Document Metadata for Labels
                      {showCopySelectionMode && (
                        <div
                          style={{
                            float: "right",
                            fontSize: "14px",
                            fontWeight: "normal",
                            color: "#666",
                            marginTop: "5px",
                          }}
                        >
                          {this.getSelectedCopiesCount()} copies selected for
                          printing
                        </div>
                      )}
                    </Header>

                    {physicalCopiesError && (
                      <Message negative>
                        <Message.Header>
                          Error Loading Physical Copies
                        </Message.Header>
                        <p>{physicalCopiesError}</p>
                        <Button
                          onClick={this.fetchPhysicalCopiesData}
                          size="small"
                        >
                          Try Again
                        </Button>
                      </Message>
                    )}

                    <Dimmer.Dimmable dimmed={loadingPhysicalCopies}>
                      <Dimmer active={loadingPhysicalCopies} inverted>
                        <Loader>Loading physical copies data...</Loader>
                      </Dimmer>

                      <style>{tableStyles}</style>

                      <Table celled compact className="document-labels-table">
                        <Table.Header>
                          <Table.Row>
                            <Table.HeaderCell width={3}>Title</Table.HeaderCell>
                            <Table.HeaderCell width={2}>
                              Library Section
                            </Table.HeaderCell>
                            <Table.HeaderCell width={2}>
                              Material Type
                            </Table.HeaderCell>
                            <Table.HeaderCell width={3}>
                              Physical Copies
                              {showCopySelectionMode && (
                                <div
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: "normal",
                                    color: "#666",
                                    marginTop: "4px",
                                  }}
                                >
                                  (Check boxes to select specific copies)
                                </div>
                              )}
                            </Table.HeaderCell>
                            <Table.HeaderCell width={2}>
                              Location
                            </Table.HeaderCell>
                            <Table.HeaderCell width={1}>
                              Actions
                            </Table.HeaderCell>
                          </Table.Row>
                        </Table.Header>

                        <Table.Body>
                          {documentsWithPhysicalCopies.map((doc) => {
                            const librarySection =
                              doc.documentInfo?.sections ||
                              doc.sections ||
                              "Not specified";
                            const materialType =
                              doc.documentInfo?.documentType ||
                              doc.document_type ||
                              "Not specified";

                            if (doc.fetchError) {
                              return (
                                <Table.Row key={doc.pid}>
                                  <Table.Cell className="title-column">
                                    {doc.title}
                                  </Table.Cell>
                                  <Table.Cell>{librarySection}</Table.Cell>
                                  <Table.Cell>{materialType}</Table.Cell>
                                  <Table.Cell colSpan="2">
                                    <div
                                      style={{
                                        color: "#d93025",
                                        fontSize: "13px",
                                      }}
                                    >
                                      {doc.fetchError}
                                    </div>
                                  </Table.Cell>
                                  <Table.Cell textAlign="center">
                                    <div className="action-buttons">
                                      <Button
                                        size="mini"
                                        icon="edit"
                                        as={Link}
                                        to={BackOfficeRoutes.documentEditFor(
                                          doc.pid
                                        )}
                                        color="blue"
                                        title="Edit Document"
                                      />
                                      <Button
                                        size="mini"
                                        icon="trash"
                                        color="red"
                                        onClick={() =>
                                          removeDocumentFromSelection(doc)
                                        }
                                        title="Remove from Selection"
                                      />
                                    </div>
                                  </Table.Cell>
                                </Table.Row>
                              );
                            }

                            const uniqueLocations = doc.physicalCopies
                              ? [
                                  ...new Set(
                                    doc.physicalCopies.map(
                                      (copy) => copy.location
                                    )
                                  ),
                                ].join(", ")
                              : "Not specified";

                            return (
                              <Table.Row key={doc.pid}>
                                <Table.Cell
                                  className="title-column"
                                  title={doc.documentInfo?.title || doc.title}
                                >
                                  {doc.documentInfo?.title || doc.title}
                                </Table.Cell>
                                <Table.Cell>{librarySection}</Table.Cell>
                                <Table.Cell>{materialType}</Table.Cell>
                                <Table.Cell className="physical-copies-cell">
                                  {this.renderPhysicalCopiesCell(doc)}
                                </Table.Cell>
                                <Table.Cell>{uniqueLocations}</Table.Cell>
                                <Table.Cell textAlign="center">
                                  <div className="action-buttons">
                                    <Button
                                      size="mini"
                                      icon="edit"
                                      as={Link}
                                      to={BackOfficeRoutes.documentEditFor(
                                        doc.pid
                                      )}
                                      color="blue"
                                      title="Edit Document"
                                    />
                                    <Button
                                      size="mini"
                                      icon="trash"
                                      color="red"
                                      onClick={() =>
                                        removeDocumentFromSelection(doc)
                                      }
                                      title="Remove from Selection"
                                    />
                                  </div>
                                </Table.Cell>
                              </Table.Row>
                            );
                          })}
                        </Table.Body>
                      </Table>
                    </Dimmer.Dimmable>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={16}>
                    <Button
                      primary
                      floated="right"
                      size="small"
                      onClick={this.handlePrintLabels}
                      disabled={!this.hasSelectedCopies()}
                    >
                      <Icon name="print" />
                      {this.hasSelectedCopies()
                        ? `Print Selected Copies (${this.getSelectedCopiesCount()})`
                        : "Print Selected Copies"}
                    </Button>

                    {!this.hasSelectedCopies() && (
                      <Message
                        warning
                        style={{ clear: "both", marginTop: "1em" }}
                      >
                        <Message.Header>No Copies Selected</Message.Header>
                        <p>
                          Please select specific copies by checking the boxes in
                          the Physical Copies column.
                        </p>
                      </Message>
                    )}

                    <Button
                      color="blue"
                      icon
                      labelPosition="left"
                      floated="right"
                      size="small"
                      as={Link}
                      to={BackOfficeRoutes.documentLabelsFormat}
                    >
                      <Icon name="edit" />
                      Edit Label Format
                    </Button>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={16}>
                    <Header as="h3">Selected Documents</Header>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row columns={2}>
                  {selectedDocuments.map((doc) => (
                    <Grid.Column key={doc.pid}>
                      <DocumentLabelsSelectedCard
                        document={doc}
                        onRemoveDocument={removeDocumentFromSelection}
                        onUpdateDocument={this.props.updateDocumentInSelection}
                      />
                    </Grid.Column>
                  ))}
                </Grid.Row>
              </>
            )}
          </Grid>
        </Container>

        <Modal
          open={this.state.showMissingCallNumberDialog}
          onClose={this.handleCancelPrint}
          size="small"
        >
          <Modal.Header>Missing Call Numbers</Modal.Header>
          <Modal.Content>
            <p>
              There are{" "}
              {this.getMissingCallNumberCount(this.props.selectedDocuments)}{" "}
              documents with missing call numbers. Are you sure you want to
              proceed with printing?
            </p>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={this.handleCancelPrint} negative>
              Cancel
            </Button>
            <Button onClick={this.handleConfirmPrint} positive>
              Proceed
            </Button>
          </Modal.Actions>
        </Modal>
      </>
    );
  }
}

DocumentLabelsSelectedComponent.propTypes = {
  selectedDocuments: PropTypes.array.isRequired,
  removeDocumentFromSelection: PropTypes.func.isRequired,
  clearDocumentSelection: PropTypes.func.isRequired,
  updateDocumentInSelection: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  selectedDocuments: state.documentLabels?.selectedDocuments || [],
});

const mapDispatchToProps = (dispatch) => ({
  removeDocumentFromSelection: (document) =>
    dispatch(removeDocumentFromSelection(document)),
  clearDocumentSelection: () => dispatch(clearDocumentSelection()),
  updateDocumentInSelection: (document) =>
    dispatch(updateDocumentInSelection(document)),
});

export const DocumentLabelsSelected = connect(
  mapStateToProps,
  mapDispatchToProps
)(DocumentLabelsSelectedComponent);
